import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Loader2, Trash2, AlertCircle, Calendar, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { cobrosExpensasApi } from '../../../lib/api';

const fmtMoney = (n) => {
  const v = Math.round(Number(n || 0));
  const sign = v < 0 ? '-' : '';
  return `${sign}$ ${Math.abs(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const fmtFecha = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-');
  if (d.length !== 3) return s;
  return `${d[2]}/${d[1]}/${d[0]}`;
};

const fmtPeriodo = (s) => {
  if (!s) return '';
  const [y, m] = s.split('-');
  const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${meses[parseInt(m, 10) - 1]} ${y}`;
};

const ESTADO_BADGE = {
  pendiente: { color: 'bg-slate-100 text-slate-700 ring-slate-200', label: 'Pendiente' },
  parcial: { color: 'bg-amber-50 text-amber-700 ring-amber-200', label: 'Parcial' },
  pagado: { color: 'bg-emerald-50 text-emerald-700 ring-emerald-200', label: 'Pagado' },
  pagado_tarde: { color: 'bg-blue-50 text-blue-700 ring-blue-200', label: 'Pagado tarde' },
  vencido: { color: 'bg-rose-50 text-rose-700 ring-rose-200', label: 'Vencido' },
  en_revision: { color: 'bg-violet-50 text-violet-700 ring-violet-200', label: 'En revisión' },
};

export function CobroDetalleDialog({ open, onClose, edificioId, periodoId, unidadId, propietarioNombre, propietarios = [], onChanged }) {
  const [loading, setLoading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [anulandoId, setAnulandoId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await cobrosExpensasApi.getUnidad(edificioId, periodoId, unidadId);
      setDoc(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando detalle');
      setDoc(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && unidadId) load();
    if (!open) setDoc(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, edificioId, periodoId, unidadId]);

  const handleAnular = async (pagoId) => {
    if (!window.confirm('¿Anular este pago? Quedará marcado como anulado pero no se elimina del historial.')) return;
    setAnulandoId(pagoId);
    try {
      await cobrosExpensasApi.anularPago(edificioId, periodoId, unidadId, pagoId, 'Anulado desde detalle');
      toast.success('Pago anulado');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al anular');
    } finally {
      setAnulandoId(null);
    }
  };

  const estadoBadge = doc?.estado ? ESTADO_BADGE[doc.estado] || ESTADO_BADGE.pendiente : null;
  const cobros = (doc?.cobros_parciales || []).slice().sort((a, b) => (b.fecha_pago || '').localeCompare(a.fecha_pago || ''));
  const breakdown = doc?.deuda_anterior_breakdown || [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cobro-detalle">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-indigo-600" />
            Detalle de cobro — {doc?.unidad_nombre || '...'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : !doc ? (
          <div className="py-10 text-center text-sm text-slate-500">No hay datos.</div>
        ) : (
          <div className="space-y-4">
            {/* Encabezado */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="text-slate-500">Unidad</div>
                <div className="font-medium text-slate-800">{doc.unidad_nombre} <span className="text-slate-400">· {doc.tipo}</span></div>
              </div>
              <div>
                <div className="text-slate-500">Responsable de pago</div>
                <div className="font-medium text-slate-800">{propietarioNombre || '—'}</div>
                {propietarios && propietarios.length > 1 && (
                  <div className="mt-1 text-[11px] text-slate-500" data-testid="detalle-propietarios">
                    <div className="font-medium text-slate-600">Propietarios:</div>
                    {propietarios.map((p, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{p.nombre || '—'}</span>
                        <span className="tabular-nums">{p.porcentaje != null ? `${p.porcentaje}%` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="text-slate-500">Período</div>
                <div className="font-medium text-slate-800">{fmtPeriodo(doc.periodo)}</div>
              </div>
              <div>
                <div className="text-slate-500">Estado</div>
                <div>
                  {estadoBadge && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${estadoBadge.color}`}>
                      {estadoBadge.label}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Liquidación */}
            <div className="border border-slate-200 rounded-lg p-3 space-y-1.5 text-sm">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Liquidación del período</div>
              <div className="flex justify-between"><span className="text-slate-600">Monto liquidado</span><span className="font-medium">{fmtMoney(doc.monto_liquidado)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Deuda anterior</span><span className="font-medium">{fmtMoney(doc.deuda_anterior)}</span></div>
              {Number(doc.intereses || 0) > 0 && (
                <div className="flex justify-between"><span className="text-slate-600">Intereses</span><span className="font-medium">{fmtMoney(doc.intereses)}</span></div>
              )}
              <div className="border-t border-slate-200 pt-1.5 flex justify-between">
                <span className="text-slate-700 font-medium">Total a cobrar</span>
                <span className="font-semibold text-slate-900">{fmtMoney(doc.total_a_cobrar)}</span>
              </div>
              <div className="flex justify-between text-emerald-700">
                <span>Total cobrado</span><span className="font-semibold">{fmtMoney(doc.total_cobrado)}</span>
              </div>
              <div className="flex justify-between">
                <span className={doc.saldo_pendiente > 0 ? 'text-rose-700 font-medium' : 'text-emerald-700 font-medium'}>Saldo final</span>
                <span className={doc.saldo_pendiente > 0 ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold'}>{fmtMoney(doc.saldo_pendiente)}</span>
              </div>
            </div>

            {/* Vencimientos */}
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-slate-50 rounded p-2">
                <div className="text-slate-500">Vto 1</div>
                <div className="font-medium text-slate-800 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtFecha(doc.fecha_vto1)}</div>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <div className="text-slate-500">Vto 2</div>
                <div className="font-medium text-slate-800 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtFecha(doc.fecha_vto2)}</div>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <div className="text-slate-500">Vto final</div>
                <div className="font-medium text-slate-800 flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtFecha(doc.fecha_vto_final)}</div>
              </div>
            </div>

            {/* Deuda anterior breakdown */}
            {breakdown.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Deuda anterior — desglose por período
                </div>
                <div className="space-y-1 text-xs">
                  {breakdown.map((b) => (
                    <div key={b.periodo_id} className="flex justify-between items-center" data-testid={`breakdown-${b.periodo}`}>
                      <div>
                        <span className="font-medium text-amber-900">{fmtPeriodo(b.periodo)}</span>
                        <span className="text-amber-700 ml-2">· Vto: {fmtFecha(b.fecha_vto_final)}</span>
                        {b.dias_vencidos > 0 && <span className="text-rose-700 ml-2">· {b.dias_vencidos}d vencidos</span>}
                      </div>
                      <span className="font-semibold text-amber-900">{fmtMoney(b.saldo_pendiente)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cobros parciales */}
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Pagos parciales</div>
              {cobros.length === 0 ? (
                <div className="text-xs text-slate-400 bg-slate-50 rounded p-3 text-center">Sin pagos registrados.</div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Fecha</th>
                        <th className="px-2 py-1.5 text-right font-medium">Monto</th>
                        <th className="px-2 py-1.5 text-left font-medium">Método</th>
                        <th className="px-2 py-1.5 text-left font-medium">Ref.</th>
                        <th className="px-2 py-1.5 text-left font-medium">Obs.</th>
                        <th className="px-2 py-1.5 text-right font-medium w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cobros.map((cp) => (
                        <tr key={cp.pago_id} className={`border-t border-slate-100 ${cp.anulado ? 'bg-rose-50/50' : ''}`} data-testid={`cobro-row-${cp.pago_id}`}>
                          <td className="px-2 py-1.5">{fmtFecha(cp.fecha_pago)}</td>
                          <td className={`px-2 py-1.5 text-right font-medium ${cp.anulado ? 'text-slate-400 line-through' : 'text-emerald-700'}`}>{fmtMoney(cp.monto)}</td>
                          <td className="px-2 py-1.5 text-slate-600">{cp.metodo_pago || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600">{cp.referencia || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-500 max-w-[180px] truncate" title={cp.observacion || ''}>{cp.observacion || (cp.anulado ? <span className="text-rose-600 italic">Anulado · {cp.motivo_anulacion || 'sin motivo'}</span> : '—')}</td>
                          <td className="px-2 py-1.5 text-right">
                            {!cp.anulado && (
                              <button
                                onClick={() => handleAnular(cp.pago_id)}
                                disabled={anulandoId === cp.pago_id}
                                className="text-rose-600 hover:text-rose-800 disabled:opacity-30"
                                title="Anular pago"
                                data-testid={`btn-anular-${cp.pago_id}`}
                              >
                                {anulandoId === cp.pago_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="btn-cerrar-detalle">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CobroDetalleDialog;
